import Phaser from 'phaser';

/**
 * Shared cartoon drawing kit.
 *
 * Every gameplay shape is built from flat fills, one bold ink keyline, and a
 * single light band. Outlines are drawn as a slightly larger shape underneath
 * the fill instead of a stroke, which keeps thick keylines crisp when the
 * 1280x720 canvas is scaled down to a small landscape phone.
 */
export const INK = 0x16233c;
export const INK_SOFT = 0x2c4363;

export function mixColor(from: number, to: number, amount: number): number {
  const clamped = Phaser.Math.Clamp(amount, 0, 1);
  const fromRed = (from >> 16) & 0xff;
  const fromGreen = (from >> 8) & 0xff;
  const fromBlue = from & 0xff;
  const toRed = (to >> 16) & 0xff;
  const toGreen = (to >> 8) & 0xff;
  const toBlue = to & 0xff;
  return (
    (Math.round(Phaser.Math.Linear(fromRed, toRed, clamped)) << 16)
    | (Math.round(Phaser.Math.Linear(fromGreen, toGreen, clamped)) << 8)
    | Math.round(Phaser.Math.Linear(fromBlue, toBlue, clamped))
  );
}

export function lighten(color: number, amount: number): number {
  return mixColor(color, 0xffffff, amount);
}

export function darken(color: number, amount: number): number {
  return mixColor(color, 0x0a1526, amount);
}

/** Rounded rectangle with a bold ink keyline drawn behind the fill. */
export function inkRect(
  graphics: Phaser.GameObjects.Graphics,
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number,
  fill: number,
  weight = 3,
  alpha = 1,
  ink = INK,
): void {
  if (weight > 0) {
    graphics.fillStyle(ink, alpha);
    graphics.fillRoundedRect(x - weight, y - weight, width + weight * 2, height + weight * 2, radius + weight);
  }
  graphics.fillStyle(fill, alpha);
  graphics.fillRoundedRect(x, y, width, height, radius);
}

export function inkCircle(
  graphics: Phaser.GameObjects.Graphics,
  x: number,
  y: number,
  radius: number,
  fill: number,
  weight = 3,
  alpha = 1,
  ink = INK,
): void {
  if (weight > 0) {
    graphics.fillStyle(ink, alpha);
    graphics.fillCircle(x, y, radius + weight);
  }
  graphics.fillStyle(fill, alpha);
  graphics.fillCircle(x, y, radius);
}

export function inkEllipse(
  graphics: Phaser.GameObjects.Graphics,
  x: number,
  y: number,
  width: number,
  height: number,
  fill: number,
  weight = 3,
  alpha = 1,
  ink = INK,
): void {
  if (weight > 0) {
    graphics.fillStyle(ink, alpha);
    graphics.fillEllipse(x, y, width + weight * 2, height + weight * 2);
  }
  graphics.fillStyle(fill, alpha);
  graphics.fillEllipse(x, y, width, height);
}

export function inkTriangle(
  graphics: Phaser.GameObjects.Graphics,
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  x3: number,
  y3: number,
  fill: number,
  weight = 3,
  alpha = 1,
  ink = INK,
): void {
  if (weight > 0) {
    const cx = (x1 + x2 + x3) / 3;
    const cy = (y1 + y2 + y3) / 3;
    const grow = (px: number, py: number): [number, number] => {
      const dx = px - cx;
      const dy = py - cy;
      const length = Math.hypot(dx, dy) || 1;
      return [px + (dx / length) * weight * 1.6, py + (dy / length) * weight * 1.6];
    };
    const [ax, ay] = grow(x1, y1);
    const [bx, by] = grow(x2, y2);
    const [gx, gy] = grow(x3, y3);
    graphics.fillStyle(ink, alpha);
    graphics.fillTriangle(ax, ay, bx, by, gx, gy);
  }
  graphics.fillStyle(fill, alpha);
  graphics.fillTriangle(x1, y1, x2, y2, x3, y3);
}

/** Closed polygon with an ink keyline, used for scarves, fins, and crowns. */
export function inkPolygon(
  graphics: Phaser.GameObjects.Graphics,
  points: readonly (readonly [number, number])[],
  fill: number,
  weight = 3,
  alpha = 1,
  ink = INK,
): void {
  const vectors = points.map(([x, y]) => new Phaser.Math.Vector2(x, y));
  if (weight > 0) {
    graphics.fillStyle(ink, alpha);
    graphics.fillPoints(vectors, true);
    graphics.lineStyle(weight * 2, ink, alpha);
    graphics.strokePoints(vectors, true);
  }
  graphics.fillStyle(fill, alpha);
  graphics.fillPoints(vectors, true);
}

/** Tapered ink-outlined stroke used for tails, limbs, and vines. */
export function inkStroke(
  graphics: Phaser.GameObjects.Graphics,
  points: readonly (readonly [number, number])[],
  fill: number,
  width: number,
  weight = 3,
  alpha = 1,
  ink = INK,
): void {
  if (points.length < 2) return;
  const vectors = points.map(([x, y]) => new Phaser.Math.Vector2(x, y));
  if (weight > 0) {
    graphics.lineStyle(width + weight * 2, ink, alpha);
    graphics.strokePoints(vectors, false);
    for (const point of vectors) {
      graphics.fillStyle(ink, alpha);
      graphics.fillCircle(point.x, point.y, (width + weight * 2) / 2);
    }
  }
  graphics.lineStyle(width, fill, alpha);
  graphics.strokePoints(vectors, false);
  for (const point of vectors) {
    graphics.fillStyle(fill, alpha);
    graphics.fillCircle(point.x, point.y, width / 2);
  }
}

/** Soft contact shadow. Cartoon shadows stay tight and slightly offset. */
export function dropShadow(
  graphics: Phaser.GameObjects.Graphics,
  x: number,
  y: number,
  width: number,
  height: number,
  alpha = 0.2,
): void {
  graphics.fillStyle(0x2a4a6b, alpha);
  graphics.fillEllipse(x, y, width, height);
}

/** Four-point sparkle used for ice, pickups, and shine accents. */
export function sparkle(
  graphics: Phaser.GameObjects.Graphics,
  x: number,
  y: number,
  size: number,
  color = 0xffffff,
  alpha = 1,
): void {
  graphics.fillStyle(color, alpha);
  graphics.fillPoints([
    new Phaser.Math.Vector2(x, y - size),
    new Phaser.Math.Vector2(x + size * 0.3, y - size * 0.3),
    new Phaser.Math.Vector2(x + size, y),
    new Phaser.Math.Vector2(x + size * 0.3, y + size * 0.3),
    new Phaser.Math.Vector2(x, y + size),
    new Phaser.Math.Vector2(x - size * 0.3, y + size * 0.3),
    new Phaser.Math.Vector2(x - size, y),
    new Phaser.Math.Vector2(x - size * 0.3, y - size * 0.3),
  ], true);
}

/** Cartoon cloud silhouette built from overlapping lobes. */
export function cloudPuff(
  graphics: Phaser.GameObjects.Graphics,
  x: number,
  y: number,
  scale: number,
  color: number,
  alpha = 1,
): void {
  graphics.fillStyle(color, alpha);
  graphics.fillCircle(x - 34 * scale, y + 6 * scale, 19 * scale);
  graphics.fillCircle(x - 12 * scale, y - 9 * scale, 26 * scale);
  graphics.fillCircle(x + 16 * scale, y - 3 * scale, 21 * scale);
  graphics.fillCircle(x + 38 * scale, y + 7 * scale, 16 * scale);
  graphics.fillRoundedRect(x - 46 * scale, y + 2 * scale, 92 * scale, 18 * scale, 9 * scale);
}

/** Overshooting pop used for spawns and ice growth. */
export function popEase(value: number): number {
  const clamped = Phaser.Math.Clamp(value, 0, 1);
  const overshoot = 1.9;
  return 1 + (overshoot + 1) * Math.pow(clamped - 1, 3) + overshoot * Math.pow(clamped - 1, 2);
}

export function easeOutCubic(value: number): number {
  return 1 - Math.pow(1 - Phaser.Math.Clamp(value, 0, 1), 3);
}

export function easeInOut(value: number): number {
  const clamped = Phaser.Math.Clamp(value, 0, 1);
  return clamped < 0.5 ? 2 * clamped * clamped : 1 - Math.pow(-2 * clamped + 2, 2) / 2;
}

/** Deterministic 0..1 hash so decoration never flickers between frames. */
export function hash01(seed: number): number {
  const value = Math.sin(seed * 127.1 + 311.7) * 43_758.545_3;
  return value - Math.floor(value);
}

// -----------------------------------------------------------------------------
// Painted-cartoon kit
//
// The flat-fill helpers above carry the arena and props. Characters get these
// extra passes so a scoop, a cheek, or an eye reads as a rounded, lit form
// instead of a sticker, while still sitting inside the same ink keyline
// language.
// -----------------------------------------------------------------------------

/**
 * Phaser tessellates a filled ellipse into 32 segments, which shows flat
 * facets once a character fills a tile. Character curves use a finer fan.
 */
export const CURVE_SMOOTHNESS = 72;

export function softEllipse(
  graphics: Phaser.GameObjects.Graphics,
  x: number,
  y: number,
  width: number,
  height: number,
): void {
  graphics.fillEllipse(x, y, width, height, CURVE_SMOOTHNESS);
}

export function softCircle(
  graphics: Phaser.GameObjects.Graphics,
  x: number,
  y: number,
  radius: number,
): void {
  graphics.fillEllipse(x, y, radius * 2, radius * 2, CURVE_SMOOTHNESS);
}

/** Key light description shared by every rounded form on a character. */
export interface BallShading {
  readonly base: number;
  readonly light: number;
  readonly dark: number;
  /** Unit direction of the key light; negative x/y is up and to the left. */
  readonly lightX: number;
  readonly lightY: number;
  readonly steps?: number;
}

/**
 * Painted sphere: concentric ellipses that shrink and drift toward the key
 * light, running dark rim to lit core. Phaser's `fillGradientStyle` colours
 * each tessellation triangle on its own, so a stacked fill is the only way to
 * get a smooth round falloff on an ellipse.
 */
export function shadedBall(
  graphics: Phaser.GameObjects.Graphics,
  x: number,
  y: number,
  radiusX: number,
  radiusY: number,
  shading: BallShading,
  alpha = 1,
): void {
  const steps = shading.steps ?? 15;
  for (let step = 0; step <= steps; step += 1) {
    const ratio = step / steps;
    // Bright cartoon shading: the base tone owns the form, the dark tone is a
    // thin rim, and the light gathers into a small core. Letting `dark` spread
    // across half the radius reads as muddy rather than rounded.
    const color = ratio < 0.18
      ? mixColor(shading.dark, shading.base, ratio / 0.18)
      : mixColor(shading.base, shading.light, Math.pow((ratio - 0.18) / 0.82, 1.7));
    const shrink = 1 - Math.pow(ratio, 0.9) * 0.88;
    graphics.fillStyle(color, alpha);
    graphics.fillEllipse(
      x + shading.lightX * ratio * radiusX * 0.52,
      y + shading.lightY * ratio * radiusY * 0.52,
      radiusX * 2 * shrink,
      radiusY * 2 * shrink,
      CURVE_SMOOTHNESS,
    );
  }
}

/** Specular highlight: a soft halo with a tight hot core inside it. */
export function gloss(
  graphics: Phaser.GameObjects.Graphics,
  x: number,
  y: number,
  width: number,
  height: number,
  color = 0xffffff,
  alpha = 0.8,
): void {
  graphics.fillStyle(color, alpha * 0.4);
  graphics.fillEllipse(x, y, width * 1.75, height * 1.75, CURVE_SMOOTHNESS);
  graphics.fillStyle(color, alpha);
  graphics.fillEllipse(x, y, width, height, CURVE_SMOOTHNESS);
}

/** Bounce light along a form's shadow edge, so it never reads as a flat blob. */
export function rimArc(
  graphics: Phaser.GameObjects.Graphics,
  x: number,
  y: number,
  radius: number,
  from: number,
  to: number,
  width: number,
  color: number,
  alpha: number,
): void {
  graphics.lineStyle(width, color, alpha);
  graphics.beginPath();
  graphics.arc(x, y, radius, from, to, false);
  graphics.strokePath();
}

/** Soft contact shading where one form sits on another. */
export function occlude(
  graphics: Phaser.GameObjects.Graphics,
  x: number,
  y: number,
  width: number,
  height: number,
  color: number,
  alpha = 0.28,
  layers = 4,
): void {
  for (let layer = layers; layer >= 1; layer -= 1) {
    graphics.fillStyle(color, alpha / layers);
    graphics.fillEllipse(x, y, width * (layer / layers), height * (layer / layers), CURVE_SMOOTHNESS);
  }
}

/**
 * Painted fill for a silhouette that is not an ellipse — a strawberry body, a
 * melon wedge. The outline is walked inward toward a point offset into the key
 * light, so the same dark-rim-to-lit-core ramp as `shadedBall` lands on an
 * arbitrary shape.
 */
export function shadedPolygon(
  graphics: Phaser.GameObjects.Graphics,
  points: readonly (readonly [number, number])[],
  shading: BallShading,
  alpha = 1,
): void {
  if (points.length < 3) return;
  const steps = shading.steps ?? 12;
  let sumX = 0;
  let sumY = 0;
  for (const [px, py] of points) {
    sumX += px;
    sumY += py;
  }
  const centreX = sumX / points.length;
  const centreY = sumY / points.length;
  let spread = 0;
  for (const [px, py] of points) spread = Math.max(spread, Math.hypot(px - centreX, py - centreY));
  const focusX = centreX + shading.lightX * spread * 0.42;
  const focusY = centreY + shading.lightY * spread * 0.42;

  for (let step = 0; step <= steps; step += 1) {
    const ratio = step / steps;
    const color = ratio < 0.18
      ? mixColor(shading.dark, shading.base, ratio / 0.18)
      : mixColor(shading.base, shading.light, Math.pow((ratio - 0.18) / 0.82, 1.7));
    const shrink = 1 - Math.pow(ratio, 0.9) * 0.88;
    graphics.fillStyle(color, alpha);
    graphics.fillPoints(
      points.map(([px, py]) => new Phaser.Math.Vector2(
        focusX + (px - focusX) * shrink,
        focusY + (py - focusY) * shrink,
      )),
      true,
    );
  }
}
